/**
 * AddItemModal - Unified modal for adding new items
 * 
 * Features:
 * - Tabbed interface: Scan, Text, Manual
 * - Scan tab: Take photo and AI identifies items
 * - Text tab: Scan receipts, recipes, or any text list for multiple items
 * - Manual tab: Type item details manually
 * - Context-aware defaults (qty=0 in Manage mode, qty=1 otherwise)
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Capacitor } from '@capacitor/core';
import { CategorySelectorModal } from './CategorySelectorModal';
import { getCategories, Category } from '../services/categoryService';
import API_BASE_URL, { API_ENDPOINTS } from '../config';
import { scrollFieldIntoView } from '../utils/scrollFieldIntoView';
import { CodeScanner } from './CodeScanner';
import { Product } from '../services/productService';
import { recordNetworkFailure, recordNetworkSuccess } from '../hooks/useNetworkRecovery';
import { DuplicateInfo } from '../services/inventoryService';
import AIConsentModal, { hasAIConsent } from './AIConsentModal';
import { useAuth } from '../contexts/AuthContext';
import { useSpace } from '../contexts/SpaceContext';
import { useMode } from '../contexts/ModeContext';
import ModalCloseButton from './ModalCloseButton';
import '../styles/components/addItemModal.css';
import '../styles/components/scanPage.css';

const isIOSNative = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';

// Smart Title Case - capitalizes words except minor words (unless first word)
const MINOR_WORDS = new Set(['a', 'an', 'the', 'and', 'but', 'or', 'nor', 'for', 'yet', 'so', 'of', 'in', 'on', 'at', 'to', 'by', 'with', 'from', 'up', 'as']);

const toTitleCase = (str: string): string => {
  return str.split(' ').map((word, index) => {
    if (!word) return word;
    const lower = word.toLowerCase();
    // Always capitalize first word, otherwise check if it's a minor word
    if (index === 0 || !MINOR_WORDS.has(lower)) {
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    }
    return lower;
  }).join(' ');
};

type TabType = 'scan' | 'receipt' | 'barcode' | 'manual';

interface AddItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaveManual: (item: NewItemData) => Promise<void>;
  onScanComplete?: () => void; // Called after scan/receipt items are saved
  token: string;
  defaultQuantity?: number; // 0 for Manage mode, 1 otherwise
  collectionId?: number | null;
  collectionName?: string;
  defaultTab?: TabType; // Which tab to show by default
  onInventoryRefresh?: (newItemId?: number) => void;
  onBarcodeDuplicate?: (info: DuplicateInfo) => void;
  hasShoppingCollection?: boolean;
}

export interface NewItemData {
  name: string;
  description: string;
  quantity: number;
  categoryIds: number[];
  color?: string;
  area?: string;
  location?: string;
  subLocation?: string;
  purchasePrice?: number;
  purchaseDate?: string;
  imageFile?: File;
}

const AddItemModal: React.FC<AddItemModalProps> = ({
  isOpen,
  onClose,
  onSaveManual,
  onScanComplete,
  token,
  defaultQuantity = 1,
  collectionId,
  collectionName,
  defaultTab = 'scan',
  onInventoryRefresh,
  onBarcodeDuplicate,
  hasShoppingCollection = false,
}) => {
  const { user } = useAuth();
  const { activeSpace } = useSpace();
  const { activeMode } = useMode();
  const userId = user?.id;

  // AI consent state
  const [showAIConsent, setShowAIConsent] = useState(false);
  const [pendingAITab, setPendingAITab] = useState<TabType | null>(null);

  const AI_TABS: TabType[] = ['scan', 'receipt', 'barcode'];

  // Load last used tab from localStorage, fallback to defaultTab
  const getInitialTab = (): TabType => {
    const savedTab = localStorage.getItem('addItemLastTab');
    if (savedTab && (savedTab === 'scan' || savedTab === 'receipt' || savedTab === 'barcode' || savedTab === 'manual')) {
      // If saved tab requires AI consent and user hasn't consented, fall back to manual
      if (AI_TABS.includes(savedTab as TabType) && !hasAIConsent(userId)) {
        return 'manual';
      }
      return savedTab as TabType;
    }
    // If default tab requires AI consent and user hasn't consented, fall back to manual
    if (AI_TABS.includes(defaultTab) && !hasAIConsent(userId)) {
      return 'manual';
    }
    return defaultTab;
  };

  // Tab state
  const [activeTab, setActiveTab] = useState<TabType>(getInitialTab);
  
  // Save tab preference when it changes
  const handleTabChange = (tab: TabType) => {
    // Check AI consent for AI-powered tabs
    if (AI_TABS.includes(tab) && !hasAIConsent(userId)) {
      setPendingAITab(tab);
      setShowAIConsent(true);
      return;
    }
    setActiveTab(tab);
    localStorage.setItem('addItemLastTab', tab);
  };

  const handleAIConsentAccept = () => {
    setShowAIConsent(false);
    if (pendingAITab) {
      setActiveTab(pendingAITab);
      localStorage.setItem('addItemLastTab', pendingAITab);
      setPendingAITab(null);
    }
  };

  const handleAIConsentDecline = () => {
    setShowAIConsent(false);
    setPendingAITab(null);
  };
  
  // Reset to saved preference when modal opens
  useEffect(() => {
    if (isOpen) {
      setActiveTab(getInitialTab());
    }
  }, [isOpen]);
  
  // Manual form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [quantity, setQuantity] = useState(defaultQuantity);
  const [categoryIds, setCategoryIds] = useState<number[]>([]);
  const [categoryLabels, setCategoryLabels] = useState<string[]>([]);
  const [color, setColor] = useState('');
  const [area, setArea] = useState('');
  const [location, setLocation] = useState('');
  const [subLocation, setSubLocation] = useState('');
  const [purchasePrice, setPurchasePrice] = useState<string>('');
  const [purchaseDate, setPurchaseDate] = useState('');
  
  // Image state (for manual tab)
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const manualNameRef = useRef<HTMLInputElement>(null);
  
  // Receipt sub-mode: 'receipt' or 'recipe'
  const [receiptSubMode, setReceiptSubMode] = useState<'receipt' | 'recipe'>('receipt');
  // Recipe scan data
  const [recipeData, setRecipeData] = useState<{ recipe_name: string; instructions: string } | null>(null);

  // Scan/Receipt state
  const [scanImage, setScanImage] = useState<File | null>(null);
  const [scanPreview, setScanPreview] = useState<string | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [scanResults, setScanResults] = useState<any[]>([]);
  const [scanError, setScanError] = useState<string | null>(null);
  const scanInputRef = useRef<HTMLInputElement>(null);
  
  // Queue state for Photo tab
  interface QueueItem {
    id: number;
    fileName: string;
    status: 'pending' | 'analyzing' | 'saving' | 'completed' | 'error';
    error?: string;
    itemNames?: string[];
    itemCount?: number;
    createdAt?: string;
    completedAt?: string;
  }
  const [processingQueue, setProcessingQueue] = useState<QueueItem[]>([]);
  const processingQueueRef = useRef<QueueItem[]>([]);

  // Toast state for barcode tab
  const [toastMessage, setToastMessage] = useState('');
  const [showToast, setShowToast] = useState(false);

  // UI state
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [detailsExpanded, setDetailsExpanded] = useState(false);
  const [locationExpanded, setLocationExpanded] = useState(false);
  const [scanLocationExpanded, setScanLocationExpanded] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Scan location state (applies to all scanned items)
  const [scanArea, setScanArea] = useState('');
  const [scanLocation, setScanLocation] = useState('');
  const [scanSubLocation, setScanSubLocation] = useState('');
  
  // Categories data
  const [allCategories, setAllCategories] = useState<Category[]>([]);

  // Load categories when modal opens
  useEffect(() => {
    if (isOpen && token && allCategories.length === 0) {
      getCategories(token, { flat: true })
        .then(cats => setAllCategories(cats))
        .catch(err => console.error('Failed to load categories:', err));
    }
  }, [isOpen, token]);

  // Reset form when modal opens (tab is already set by getInitialTab)
  useEffect(() => {
    if (isOpen) {
      resetManualForm();
      resetScanState();
    }
  }, [isOpen, defaultQuantity]);

  // Auto-focus Name input when manual tab is active
  useEffect(() => {
    if (isOpen && activeTab === 'manual') {
      // Small delay to ensure the input is rendered
      const timer = setTimeout(() => manualNameRef.current?.focus(), 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen, activeTab]);

  // iOS keyboard handling - toggle keyboard-visible class when keyboard shows/hides
  const [keyboardVisible, setKeyboardVisible] = useState(false);
  const [isKeyboardTransitioning, setIsKeyboardTransitioning] = useState(false);
  
  useEffect(() => {
    if (!isOpen || !isIOSNative) return;
    
    const { Keyboard } = require('@capacitor/keyboard');
    
    const showListener = Keyboard.addListener('keyboardWillShow', () => {
      setIsKeyboardTransitioning(true);
      setKeyboardVisible(true);
      // Clear transition flag after animation completes
      setTimeout(() => setIsKeyboardTransitioning(false), 400);
    });
    
    const hideListener = Keyboard.addListener('keyboardWillHide', () => {
      setIsKeyboardTransitioning(true);
      setKeyboardVisible(false);
      // Clear transition flag after animation completes
      setTimeout(() => setIsKeyboardTransitioning(false), 400);
    });
    
    return () => {
      showListener.then((l: any) => l.remove());
      hideListener.then((l: any) => l.remove());
    };
  }, [isOpen]);

  // Queue polling for Photo tab
  useEffect(() => {
    if (!isOpen || !token) return;
    
    const fetchQueue = async () => {
      try {
        const response = await fetch(API_ENDPOINTS.QUEUE_PENDING, {
          headers: { 'Authorization': `Bearer ${token}` },
          keepalive: true,
        });
        if (!response.ok) return;
        
        const data = await response.json();
        const newItems = Array.isArray(data) ? data : (data.queue || []);
        
        // Check for newly completed items
        const newlyCompleted = newItems.filter((newItem: any) => {
          const oldItem = processingQueueRef.current.find(old => old.id === newItem.id);
          return newItem.status === 'completed' && (!oldItem || oldItem.status !== 'completed');
        });
        
        if (newlyCompleted.length > 0) {
          onScanComplete?.();
        }
        
        // Update ref for next comparison
        processingQueueRef.current = newItems.map((item: any) => ({
          id: item.id,
          fileName: item.file_name,
          status: item.status,
          error: item.error_message,
          itemNames: item.item_names,
          itemCount: item.item_count,
          createdAt: item.created_at,
          completedAt: item.completed_at,
        }));
        
        // Filter to show recent items
        const now = Date.now();
        const queueItems: QueueItem[] = newItems
          .filter((item: any) => {
            if (item.status !== 'completed') return true;
            const completedAt = new Date(item.completed_at || item.updated_at).getTime();
            return (now - completedAt) < 10000;
          })
          .map((item: any) => ({
            id: item.id,
            fileName: item.file_name,
            status: item.status,
            error: item.error_message,
            itemNames: item.item_names,
            itemCount: item.item_count,
            createdAt: item.created_at,
            completedAt: item.completed_at,
          }));
        
        setProcessingQueue(queueItems);
        recordNetworkSuccess();
      } catch (e) {
        console.error('Error fetching queue:', e);
        recordNetworkFailure();
      }
    };
    
    fetchQueue();
    
    if (processingQueue.length === 0) return;
    
    const pollInterval = setInterval(fetchQueue, 1000);
    return () => clearInterval(pollInterval);
  }, [isOpen, token, onScanComplete, processingQueue.length]);

  const resetManualForm = () => {
    setName('');
    setDescription('');
    setQuantity(defaultQuantity);
    setCategoryIds([]);
    setCategoryLabels([]);
    setColor('');
    setArea('');
    setLocation('');
    setSubLocation('');
    setPurchasePrice('');
    setPurchaseDate('');
    setImageFile(null);
    setImagePreview(null);
    setDetailsExpanded(false);
    setLocationExpanded(false);
    setError(null);
  };

  const resetScanState = () => {
    setScanImage(null);
    setScanPreview(null);
    setScanResults([]);
    setScanError(null);
    setIsScanning(false);
    setScanArea('');
    setScanLocation('');
    setScanSubLocation('');
    setScanLocationExpanded(false);
    setRecipeData(null);
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleScanImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !token) return;
    
    // For Scan tab (not receipt), use AUTO mode - upload and queue immediately
    if (activeTab === 'scan') {
      setIsScanning(true);
      setScanError(null);
      
      // Show preview while uploading
      const reader = new FileReader();
      reader.onload = (e) => {
        setScanPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
      
      try {
        // Step 1: Upload image to get image_id
        const uploadFormData = new FormData();
        uploadFormData.append('image', file);
        uploadFormData.append('create_image', 'true');
        uploadFormData.append('upload_only', 'true');
        
        const uploadResponse = await fetch(API_ENDPOINTS.ANALYZE, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: uploadFormData,
        });
        
        if (!uploadResponse.ok) {
          if (uploadResponse.status === 409) {
            const errorData = await uploadResponse.json();
            if (errorData.duplicate) {
              setScanError(`Duplicate image - ${errorData.existing_items_count} item(s) already exist`);
              setIsScanning(false);
              return;
            }
          }
          throw new Error('Failed to upload image');
        }
        
        const uploadData = await uploadResponse.json();
        const imageId = uploadData.image?.id;
        
        if (!imageId) {
          throw new Error('No image ID returned');
        }
        
        // Step 2: Add to processing queue
        const queueFormData = new FormData();
        queueFormData.append('file_name', file.name);
        queueFormData.append('area', scanArea.trim());
        queueFormData.append('location', scanLocation.trim());
        queueFormData.append('sub_location', scanSubLocation.trim());
        queueFormData.append('image_id', imageId.toString());
        if (collectionId) {
          queueFormData.append('collection_id', collectionId.toString());
        }
        if (activeSpace?.id) {
          queueFormData.append('space_id', activeSpace.id.toString());
        }
        
        const queueResponse = await fetch(API_ENDPOINTS.QUEUE_ADD, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: queueFormData,
        });
        
        if (!queueResponse.ok) {
          throw new Error('Failed to add to queue');
        }
        
        // Success! Show confirmation and allow adding more
        setScanResults([{ id: 1, name: 'Image queued for processing', status: 'queued' }]);
        onScanComplete?.();
        
        // Reset for next scan after a brief delay
        setTimeout(() => {
          resetScanState();
        }, 1500);
        
      } catch (err: any) {
        setScanError(err.message || 'Failed to process image');
      } finally {
        setIsScanning(false);
      }
    } else {
      // Receipt mode - auto-analyze immediately after photo selection
      setScanImage(file);
      setScanError(null);
      setIsScanning(true);
      
      // Show preview while analyzing
      const reader = new FileReader();
      reader.onload = (e) => {
        setScanPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
      
      // Auto-trigger analysis
      try {
        const formData = new FormData();
        formData.append('image', file);
        if (receiptSubMode === 'recipe') {
          formData.append('mode', 'recipe');
        }
        
        const response = await fetch(API_ENDPOINTS.SCAN_RECEIPT, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData,
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || `Failed to analyze ${receiptSubMode}`);
        }
        
        const data = await response.json();
        if (data.mode === 'recipe') {
          // Recipe mode: ingredients become scan results, store recipe metadata
          setRecipeData({
            recipe_name: data.recipe_name || 'Untitled Recipe',
            instructions: data.instructions || '',
          });
          setScanResults((data.ingredients || []).map((item: any, idx: number) => ({
            ...item,
            id: idx,
            selected: true,
          })));
        } else {
          // Receipt mode (default)
          setScanResults((data.items || []).map((item: any, idx: number) => ({
            ...item,
            id: idx,
            selected: true,
          })));
        }
      } catch (err: any) {
        setScanError(err.message || `Failed to analyze ${receiptSubMode}`);
      } finally {
        setIsScanning(false);
      }
    }
  };

  const handleScanAnalyze = async () => {
    if (!scanImage || !token) return;
    
    setIsScanning(true);
    setScanError(null);
    
    try {
      const formData = new FormData();
      formData.append('image', scanImage);
      
      const endpoint = activeTab === 'receipt' 
        ? API_ENDPOINTS.SCAN_RECEIPT 
        : API_ENDPOINTS.ANALYZE;
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to analyze image');
      }
      
      const data = await response.json();
      
      if (activeTab === 'receipt') {
        // Receipt scan returns items array
        setScanResults((data.items || []).map((item: any, idx: number) => ({
          ...item,
          id: idx,
          selected: true,
        })));
      } else {
        // Item scan returns items array
        setScanResults((data.items || []).map((item: any, idx: number) => ({
          ...item,
          id: idx,
          selected: true,
          image_id: data.image_id,
        })));
      }
    } catch (err: any) {
      setScanError(err.message || 'Failed to analyze image');
    } finally {
      setIsScanning(false);
    }
  };

  const handleSaveScanResults = async () => {
    const selectedItems = scanResults.filter(item => item.selected);
    if (selectedItems.length === 0) {
      setScanError('Please select at least one item');
      return;
    }
    
    setIsSaving(true);
    setScanError(null);
    
    try {
      if (activeTab === 'receipt') {
        // Save receipt/recipe items
        const isRecipe = receiptSubMode === 'recipe' && recipeData;
        const requestBody: any = {
          items: selectedItems.map(item => ({
            name: item.name,
            quantity: isRecipe ? -1 : (item.quantity || 1),
            price: isRecipe ? undefined : item.price,
            categoryIds: item.categoryIds || [],
          })),
          duplicate_action: isRecipe ? 'use_existing' : 'update_quantity_price',
        };
        
        if (collectionId) {
          requestBody.collection_id = collectionId;
        }
        
        // Add space_id to scope receipt items to the active hierarchical space
        requestBody.space_id = activeSpace?.id;
        
        const response = await fetch(API_ENDPOINTS.ADD_RECEIPT_TO_COLLECTION, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to save items');
        }
        
        // If recipe mode, auto-create a bindle with the recipe name and instructions
        if (isRecipe && recipeData) {
          try {
            const searchTerms = selectedItems.map(item => `"${item.name}"`).join(', ');
            const bindleResp = await fetch(API_ENDPOINTS.CREATE_BINDLE, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                name: recipeData.recipe_name,
                search_string: searchTerms,
                description: recipeData.instructions,
                collection_id: collectionId,
              }),
            });
            // Signal bindle list to refresh
            window.dispatchEvent(new CustomEvent('bindles-updated'));
          } catch (bindleErr) {
            console.warn('Failed to create recipe bindle:', bindleErr);
          }
        }
      } else {
        // Save scanned items
        const requestBody: any = {
          items: selectedItems.map(item => ({
            name: item.name,
            description: item.description || '',
            quantity: item.quantity || 1,
            image_id: item.image_id,
            collection_id: collectionId,
          })),
          area: scanArea.trim(),
          location: scanLocation.trim(),
          sub_location: scanSubLocation.trim(),
        };
        
        const response = await fetch(API_ENDPOINTS.SAVE_ITEMS, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...requestBody,
            space_id: activeSpace?.id
          }),
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to save items');
        }
      }
      
      onScanComplete?.();
      onClose();
    } catch (err: any) {
      setScanError(err.message || 'Failed to save items');
    } finally {
      setIsSaving(false);
    }
  };

  const toggleScanItemSelection = (id: number) => {
    setScanResults(prev => prev.map(item => 
      item.id === id ? { ...item, selected: !item.selected } : item
    ));
  };

  const handleCategoryChange = (ids: number[]) => {
    setCategoryIds(ids);
    const labels = ids.map(id => {
      const cat = allCategories.find(c => c.id === id);
      return cat?.label || '';
    }).filter(Boolean);
    setCategoryLabels(labels);
  };

  const handleManualSubmit = async () => {
    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      await onSaveManual({
        name: name.trim(),
        description: description.trim(),
        quantity,
        categoryIds,
        color: color.trim() || undefined,
        area: area.trim() || undefined,
        location: location.trim() || undefined,
        subLocation: subLocation.trim() || undefined,
        purchasePrice: purchasePrice ? parseFloat(purchasePrice) : undefined,
        purchaseDate: purchaseDate || undefined,
        imageFile: imageFile || undefined,
      });
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save item');
    } finally {
      setIsSaving(false);
    }
  };

  // Barcode product scan handler - saves item, delegates duplicates to InventoryView's DuplicateItemDialog
  const handleBarcodeProductScanned = useCallback(async (product: Product) => {
    if (!token) return;
    try {
      // If product has an image URL, ingest it to get an image_id
      let imageId: number | undefined;
      if (product.image_url) {
        try {
          const imgResp = await fetch(API_ENDPOINTS.CREATE_IMAGE_FROM_URL, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ image_url: product.image_url, skip_upload: true }),
          });
          if (imgResp.ok) {
            const imgData = await imgResp.json();
            imageId = imgData.id;
          }
        } catch (imgErr) {
          console.warn('Failed to ingest product image, saving without image:', imgErr);
        }
      }

      const itemData: any = {
        name: product.name,
        description: product.brand
          ? `${product.brand}${product.size ? ' - ' + product.size : ''}`
          : product.size || '',
        quantity: 1,
        category_ids: [],
      };
      if (imageId) {
        itemData.image_id = imageId;
      }
      if (collectionId) {
        itemData.collection_id = collectionId;
      }
      const response = await fetch(API_ENDPOINTS.SAVE_ITEMS, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [itemData], area: '', location: '', space_id: activeSpace?.id }),
      });
      if (!response.ok) {
        if (response.status === 409) {
          const errorData = await response.json();
          if (errorData.duplicate_item_id && onBarcodeDuplicate) {
            onBarcodeDuplicate({
              duplicate_item_id: errorData.duplicate_item_id,
              duplicate_item_name: errorData.duplicate_item_name,
              duplicate_item_quantity: errorData.duplicate_item_quantity ?? 1,
              duplicate_collection_ids: errorData.duplicate_collection_ids ?? [],
              newItemData: {
                name: product.name,
                description: product.brand
                  ? `${product.brand}${product.size ? ' - ' + product.size : ''}`
                  : product.size || '',
                quantity: 1,
                category_ids: [],
                ...(imageId ? { image_id: imageId } : {}),
              },
            });
            return;
          }
        }
        throw new Error('Failed to add item');
      }
      recordNetworkSuccess();
      const respData = await response.json();
      const newItemId = respData.saved_items?.[0]?.id;
      onInventoryRefresh?.(newItemId);
      setToastMessage(`✅ Added ${product.name}`);
      setShowToast(true);
    } catch (err) {
      console.error('Failed to save barcode product:', err);
      recordNetworkFailure();
      setToastMessage('❌ Failed to add product');
      setShowToast(true);
    }
  }, [token, collectionId, onInventoryRefresh, onBarcodeDuplicate]);

  // Barcode location scan handler
  const handleBarcodeLocationScanned = useCallback(async (location: { area: string; location: string; sub_location: string }) => {
    const qrCode = location.sub_location;
    if (!qrCode || !token) return;
    sessionStorage.setItem('qr_filter', qrCode);
    onInventoryRefresh?.();
  }, [token, onInventoryRefresh]);

  if (!isOpen) return null;

  const renderTabs = () => (
    <div className="add-item-tabs">
      <button
        className={`add-item-tab ${activeTab === 'scan' ? 'active' : ''}`}
        onClick={() => { handleTabChange('scan'); resetScanState(); }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
          <circle cx="12" cy="13" r="4"></circle>
        </svg>
        Photo
      </button>
      <button
        className={`add-item-tab ${activeTab === 'receipt' ? 'active' : ''}`}
        onClick={() => { handleTabChange('receipt'); resetScanState(); }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
          <polyline points="14 2 14 8 20 8"></polyline>
          <line x1="16" y1="13" x2="8" y2="13"></line>
          <line x1="16" y1="17" x2="8" y2="17"></line>
          <polyline points="10 9 9 9 8 9"></polyline>
        </svg>
        Text
      </button>
      <button
        className={`add-item-tab ${activeTab === 'barcode' ? 'active' : ''}`}
        onClick={() => handleTabChange('barcode')}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="2" y="4" width="2" height="16"></rect>
          <rect x="6" y="4" width="1" height="16"></rect>
          <rect x="9" y="4" width="2" height="16"></rect>
          <rect x="13" y="4" width="3" height="16"></rect>
          <rect x="18" y="4" width="1" height="16"></rect>
          <rect x="21" y="4" width="1" height="16"></rect>
        </svg>
        Barcode
      </button>
      <button
        className={`add-item-tab ${activeTab === 'manual' ? 'active' : ''}`}
        onClick={() => { handleTabChange('manual'); resetManualForm(); }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 20h9"></path>
          <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
        </svg>
        Manual
      </button>
    </div>
  );

  const renderScanTab = () => (
    <div className="add-item-scan-content">
      {/* Location - Collapsible (set BEFORE taking photo for auto mode) */}
      <button 
        className="add-item-section-header"
        onClick={() => setScanLocationExpanded(!scanLocationExpanded)}
      >
        <svg 
          width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
          style={{ transform: scanLocationExpanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
        >
          <polyline points="9 18 15 12 9 6" />
        </svg>
        <span>Location</span>
        {!scanLocationExpanded && (scanArea || scanLocation || scanSubLocation) && (
          <span className="add-item-section-indicator">•</span>
        )}
      </button>
      {scanLocationExpanded && (
        <div className="add-item-section-content">
          <div className="add-item-field-row">
            <div className="add-item-field">
              <label>Area</label>
              <input
                type="text"
                value={scanArea}
                onChange={(e) => setScanArea(e.target.value)}
                placeholder="e.g., kitchen"
              />
            </div>
            <div className="add-item-field">
              <label>Location</label>
              <input
                type="text"
                value={scanLocation}
                onChange={(e) => setScanLocation(e.target.value)}
                placeholder="e.g., pantry"
              />
            </div>
          </div>
          <div className="add-item-field">
            <label>Sub-location</label>
            <input
              type="text"
              value={scanSubLocation}
              onChange={(e) => setScanSubLocation(e.target.value)}
              placeholder="e.g., shelf 2"
            />
          </div>
        </div>
      )}

      {isScanning ? (
        <div className="add-item-scan-processing">
          {scanPreview && <img src={scanPreview} alt="Processing" className="add-item-scan-processing-image" />}
          <div className="add-item-scan-processing-status">
            <div className="add-item-scan-spinner"></div>
            <span>{activeTab === 'receipt'
              ? (receiptSubMode === 'recipe' ? 'Analyzing recipe...' : 'Analyzing receipt...')
              : 'Uploading and queuing for processing...'}</span>
          </div>
        </div>
      ) : scanResults.length > 0 && scanResults[0]?.status === 'queued' ? (
        <div className="add-item-scan-success">
          {scanPreview && <img src={scanPreview} alt="Queued" className="add-item-scan-success-image" />}
          <div className="add-item-scan-success-message">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#34C759" strokeWidth="2">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
            <span>Image queued for processing!</span>
          </div>
        </div>
      ) : !scanPreview ? (
        <div className="add-item-scan-upload" style={activeTab === 'receipt' ? { flexDirection: 'column' } : undefined}>
          <button 
            className="add-item-scan-button"
            onClick={() => scanInputRef.current?.click()}
            disabled={isScanning}
          >
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
              <circle cx="12" cy="13" r="4"></circle>
            </svg>
            <span>{activeTab === 'receipt'
              ? (receiptSubMode === 'recipe' ? 'Take photo of recipe' : 'Take photo of receipt')
              : 'Take photo of item(s)'}</span>
            <span className="add-item-scan-hint">{activeTab === 'receipt'
              ? (receiptSubMode === 'recipe' ? 'AI will extract ingredients and instructions' : 'AI will extract items from receipt')
              : 'Center items in frame • AI will identify automatically'}</span>
          </button>
          <input
            ref={scanInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleScanImageSelect}
            style={{ display: 'none' }}
          />
        </div>
      ) : (
        <div className="add-item-scan-results">
          {/* Recipe header — show recipe name and instructions preview */}
          {recipeData && (
            <div style={{
              padding: '10px 12px',
              background: 'var(--color-bg-secondary)',
              borderRadius: 8,
              marginBottom: 8,
            }}>
              <div style={{
                fontWeight: '700',
                fontSize: 'var(--font-size-md)',
                color: 'var(--color-text-primary)',
                marginBottom: 4,
              }}>
                {recipeData.recipe_name}
              </div>
              {recipeData.instructions && (
                <div style={{
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--color-text-secondary)',
                  lineHeight: '1.4',
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical' as const,
                  whiteSpace: 'pre-wrap',
                }}>
                  {recipeData.instructions}
                </div>
              )}
            </div>
          )}
          <div className="add-item-scan-results-header">
            <span>{scanResults.filter(i => i.selected).length} of {scanResults.length} {recipeData ? 'ingredients' : 'items'} selected</span>
          </div>
          <div className="add-item-scan-results-list">
            {scanResults.map(item => (
              <label key={item.id} className="add-item-scan-result-item">
                <input
                  type="checkbox"
                  checked={item.selected}
                  onChange={() => toggleScanItemSelection(item.id)}
                />
                <span className="add-item-scan-result-name">{item.name}</span>
                {recipeData && item.quantity && (
                  <span className="add-item-scan-result-qty" style={{ color: 'var(--color-text-muted)' }}>{item.quantity}{item.unit ? ` ${item.unit}` : ''}</span>
                )}
                {!recipeData && item.quantity > 1 && (
                  <span className="add-item-scan-result-qty">×{item.quantity}</span>
                )}
                {!recipeData && item.price && (
                  <span className="add-item-scan-result-price">${item.price}</span>
                )}
              </label>
            ))}
          </div>
          
          <div className="add-item-scan-actions">
            <button 
              className="add-item-btn-secondary"
              onClick={resetScanState}
              disabled={isSaving}
            >
              Start Over
            </button>
            <button 
              className="add-item-btn-primary"
              onClick={handleSaveScanResults}
              disabled={isSaving || scanResults.filter(i => i.selected).length === 0}
            >
              {isSaving ? 'Saving...' : recipeData
                ? `Add Recipe + ${scanResults.filter(i => i.selected).length} Ingredients`
                : `Add ${scanResults.filter(i => i.selected).length} Items`}
            </button>
          </div>
        </div>
      )}
      {scanError && <div className="add-item-error">{scanError}</div>}
      
      {/* Processing Queue */}
      {processingQueue.length > 0 && (
        <div className="scan-queue-section">
          <div className="scan-queue-header">
            <span className="scan-queue-title">Processing Queue ({processingQueue.length})</span>
          </div>
          <div className="scan-queue-items">
            {[...processingQueue].reverse().map((item, index) => {
              const processingTime = item.completedAt && item.createdAt
                ? Math.round((new Date(item.completedAt).getTime() - new Date(item.createdAt).getTime()) / 1000)
                : null;
              const originalIndex = processingQueue.length - 1 - index;
              const displayName = item.status === 'completed' && item.itemNames && item.itemNames.length > 0
                ? item.itemNames.slice(0, 2).join(', ') + (item.itemNames.length > 2 ? ` +${item.itemNames.length - 2}` : '')
                : item.status === 'pending'
                  ? `#${originalIndex + 1} in queue`
                  : 'Processing...';
              return (
                <div key={item.id} className={`scan-queue-item scan-queue-item--${item.status}`}>
                  <span className="scan-queue-item-name">{displayName}</span>
                  <span className="scan-queue-item-status">
                    {item.status === 'pending' && '⏳ Waiting'}
                    {item.status === 'analyzing' && '🔍 Analyzing...'}
                    {item.status === 'saving' && '💾 Saving...'}
                    {item.status === 'completed' && (
                      <>✅ {item.itemCount || 0} item{item.itemCount !== 1 ? 's' : ''}{processingTime ? ` · ${processingTime}s` : ''}</>
                    )}
                    {item.status === 'error' && `❌ ${item.error || 'Error'}`}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );

  const renderManualTab = () => (
    <>
      {error && <div className="add-item-error">{error}</div>}

      {/* Image Section */}
      <div className="add-item-image-section">
        {imagePreview ? (
          <div className="add-item-image-preview">
            <img src={imagePreview} alt="Preview" />
            <button className="add-item-image-remove" onClick={handleRemoveImage}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        ) : (
          <button 
            className="add-item-image-button"
            onClick={() => fileInputRef.current?.click()}
          >
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <polyline points="21 15 16 10 5 21" />
            </svg>
            <span>Add Photo</span>
          </button>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageSelect}
          style={{ display: 'none' }}
        />
      </div>

      {/* Name */}
      <div className="add-item-field">
        <label>Name *</label>
        <input
          ref={manualNameRef}
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={(e) => setName(toTitleCase(e.target.value))}
          onFocus={(e) => scrollFieldIntoView(e.target)}
          placeholder="Item name"
        />
      </div>

      {/* Description */}
      <div className="add-item-field">
        <label>Description</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          onFocus={(e) => scrollFieldIntoView(e.target)}
          placeholder="Optional description"
          rows={2}
        />
      </div>

      {/* Categories */}
      <button 
        className="add-item-section-button"
        onClick={() => setShowCategoryModal(true)}
      >
        <span className="add-item-section-label">
          Categories
          {categoryIds.length > 0 && (
            <span className="add-item-section-count">({categoryIds.length})</span>
          )}
        </span>
        {categoryLabels.length > 0 ? (
          <span className="add-item-section-preview">
            {categoryLabels.slice(0, 2).join(', ')}
            {categoryLabels.length > 2 && ` +${categoryLabels.length - 2}`}
          </span>
        ) : (
          <span className="add-item-section-hint">Tap to select</span>
        )}
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </button>

      {/* Details - Collapsible */}
      <button 
        className="add-item-section-header"
        onClick={() => setDetailsExpanded(!detailsExpanded)}
      >
        <svg 
          width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
          style={{ transform: detailsExpanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
        >
          <polyline points="9 18 15 12 9 6" />
        </svg>
        <span>Details</span>
        {!detailsExpanded && (quantity !== defaultQuantity || color || purchasePrice || purchaseDate) && (
          <span className="add-item-section-indicator">•</span>
        )}
      </button>
      {detailsExpanded && (
        <div className="add-item-section-content">
          <div className="add-item-field-row">
            <div className="add-item-field add-item-field-narrow">
              <label>Quantity</label>
              <input
                type="number"
                min="0"
                value={quantity}
                onChange={(e) => setQuantity(parseInt(e.target.value) || 0)}
                onFocus={(e) => scrollFieldIntoView(e.target, { selectText: true })}
              />
            </div>
            <div className="add-item-field add-item-field-wide">
              <label>Color</label>
              <input
                type="text"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                onFocus={(e) => scrollFieldIntoView(e.target)}
                placeholder="e.g., red"
              />
            </div>
          </div>
          <div className="add-item-field-row">
            <div className="add-item-field add-item-field-wide">
              <label>Price</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={purchasePrice}
                onChange={(e) => setPurchasePrice(e.target.value)}
                onFocus={(e) => scrollFieldIntoView(e.target)}
                placeholder="0.00"
              />
            </div>
            <div className="add-item-field add-item-field-narrow">
              <label>Purchase Date</label>
              <input
                type="date"
                value={purchaseDate}
                onChange={(e) => setPurchaseDate(e.target.value)}
                onFocus={(e) => scrollFieldIntoView(e.target)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Location - Collapsible */}
      <button 
        className="add-item-section-header"
        onClick={() => setLocationExpanded(!locationExpanded)}
      >
        <svg 
          width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
          style={{ transform: locationExpanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
        >
          <polyline points="9 18 15 12 9 6" />
        </svg>
        <span>Location</span>
        {!locationExpanded && (area || location || subLocation) && (
          <span className="add-item-section-indicator">•</span>
        )}
      </button>
      {locationExpanded && (
        <div className="add-item-section-content">
          <div className="add-item-field-row">
            <div className="add-item-field">
              <label>Area</label>
              <input
                type="text"
                value={area}
                onChange={(e) => setArea(e.target.value)}
                onFocus={(e) => scrollFieldIntoView(e.target)}
                placeholder="e.g., kitchen"
              />
            </div>
            <div className="add-item-field">
              <label>Location</label>
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                onFocus={(e) => scrollFieldIntoView(e.target)}
                placeholder="e.g., pantry"
              />
            </div>
          </div>
          <div className="add-item-field">
            <label>Sub-location</label>
            <input
              type="text"
              value={subLocation}
              onChange={(e) => setSubLocation(e.target.value)}
              onFocus={(e) => scrollFieldIntoView(e.target)}
              placeholder="e.g., top shelf"
            />
          </div>
        </div>
      )}
    </>
  );

  const renderBarcodeTab = () => (
    <div className="add-item-scan-content">
      {token && (
        <div className="add-item-scan-upload">
          <CodeScanner
            token={token}
            onProductScanned={handleBarcodeProductScanned}
            onLocationScanned={handleBarcodeLocationScanned}
            onShowToast={(message) => {
              setToastMessage(message);
              setShowToast(true);
            }}
            buttonText="Scan barcode or QR code"
            buttonClassName="add-item-scan-button"
            hasShoppingCollection={hasShoppingCollection}
          />
        </div>
      )}
    </div>
  );

  // Handle overlay click - prevent closing during keyboard transitions
  const handleOverlayClick = (e: React.MouseEvent) => {
    // Don't close if keyboard is transitioning (prevents accidental closes on iOS)
    if (isKeyboardTransitioning) {
      e.stopPropagation();
      return;
    }
    onClose();
  };

  return createPortal(
    <div className={`add-item-modal-overlay ${isIOSNative ? 'ios-native' : ''} ${keyboardVisible ? 'keyboard-visible' : ''}`} onClick={handleOverlayClick}>
      <div className={`add-item-modal ${isIOSNative ? 'ios-native' : ''} ${keyboardVisible ? 'keyboard-visible' : ''}`} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="add-item-modal-header">
          <h2>Add Items</h2>
          {collectionName && (
            <span className="add-item-collection-badge">→ {collectionName}</span>
          )}
          <ModalCloseButton onClick={onClose} />
        </div>

        {/* Tabs */}
        {renderTabs()}

        {/* Scrollable Content */}
        <div className="add-item-modal-content">
          {activeTab === 'manual' ? renderManualTab() : activeTab === 'barcode' ? renderBarcodeTab() : renderScanTab()}
        </div>

        {/* Footer - only for manual tab */}
        {activeTab === 'manual' && (
          <div className="add-item-modal-footer">
            <button 
              className="add-item-btn-cancel"
              onClick={onClose}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button 
              className="add-item-btn-save"
              onClick={handleManualSubmit}
              disabled={isSaving || !name.trim()}
            >
              {isSaving ? 'Saving...' : 'Add Item'}
            </button>
          </div>
        )}
      </div>

      {/* Category Selector Modal */}
      {showCategoryModal && (
        <CategorySelectorModal
          isOpen={showCategoryModal}
          onClose={() => setShowCategoryModal(false)}
          selectedCategoryIds={categoryIds}
          onChange={handleCategoryChange}
          token={token}
          allCategories={allCategories}
        />
      )}

      {/* AI Data Consent Modal */}
      <AIConsentModal
        isOpen={showAIConsent}
        onAccept={handleAIConsentAccept}
        onDecline={handleAIConsentDecline}
        userId={userId}
      />

    </div>,
    document.body
  );
};

export default AddItemModal;
